import { useMediaQuery } from 'react-responsive';
import { useEffect, useState, useRef } from 'preact/hooks';

export interface VideoProgressCircleProps {
  duration: number | undefined;
  color?: string;
  size: string;
  isPlaying?: boolean;
  getCurrentTime: () => number;
  onSeek: (time: number) => void;
}

export const CircularMediaProgress = ({
  duration,
  color = '#3b82f6',
  isPlaying,
  getCurrentTime,
  onSeek,
  size,
}: VideoProgressCircleProps) => {
  if (!duration) return null;
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const STROKE_WIDTH = 2;
  const RADIUS = 50;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
  const [currentTime, setCurrentTime] = useState<number | undefined>(
    getCurrentTime(),
  );
  const animationFrameRef = useRef<number>();
  const lastUpdateRef = useRef<number>(0);
  const svgRef = useRef<SVGSVGElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (!isPlaying) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      return;
    }

    const updateProgress = (timestamp: number) => {
      if (!lastUpdateRef.current) {
        lastUpdateRef.current = timestamp;
      }

      // Update every 16ms (roughly 60fps) for smooth animation
      if (timestamp - lastUpdateRef.current >= 16) {
        setCurrentTime(getCurrentTime());
        lastUpdateRef.current = timestamp;
      }

      animationFrameRef.current = requestAnimationFrame(updateProgress);
    };

    animationFrameRef.current = requestAnimationFrame(updateProgress);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [getCurrentTime, isPlaying]);

  // Calculate seek position from mouse/touch event
  const calculateSeekTime = (clientX: number, clientY: number) => {
    if (!svgRef.current) return 0;

    const rect = svgRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    // Calculate angle from center
    const dx = clientX - centerX;
    const dy = clientY - centerY;
    let angle = Math.atan2(dy, dx);

    // Convert to 0-360 degrees, starting from top (like clock)
    angle = (angle + Math.PI * 2.5) % (Math.PI * 2);
    if (angle < 0) angle += Math.PI * 2;

    // Convert angle to time
    const progress = angle / (Math.PI * 2);
    return Math.max(0, Math.min(duration, progress * duration));
  };

  const handleMouseDown = (e: React.MouseEvent<Element>) => {
    e.preventDefault();
    e.stopPropagation();
    const seekTime = calculateSeekTime(e.clientX, e.clientY);
    setCurrentTime(seekTime);
    onSeek(seekTime);
  };

  const progress = duration && currentTime ? currentTime / duration : 0;
  const showProgressBar = !(isPlaying === false && currentTime === 0);
  const strokeWidth = isHovered || isMobile ? STROKE_WIDTH * 2 : STROKE_WIDTH;

  return (
    <svg
      ref={svgRef}
      width={size}
      height={size}
      viewBox="0 0 100 100"
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 99,
        overflow: 'visible',
        opacity: showProgressBar ? 1 : 0,
        transition: 'opacity 0.6s ease-in-out',
        pointerEvents: 'none',
      }}
    >
      {/* Background circle */}
      <circle
        cx="50"
        cy="50"
        r={RADIUS}
        stroke="#eee"
        strokeWidth={strokeWidth}
        fill="none"
        style={{
          pointerEvents: 'visibleStroke',
          cursor: 'pointer',
        }}
        onPointerEnter={() => !isMobile && setIsHovered(true)}
        onPointerLeave={() => !isMobile && setIsHovered(false)}
        onPointerDown={handleMouseDown}
      />
      {/* Progress circle */}
      <circle
        cx="50"
        cy="50"
        r={RADIUS}
        stroke={color}
        strokeWidth={strokeWidth}
        fill="none"
        strokeDasharray={CIRCUMFERENCE}
        strokeDashoffset={(1 - progress) * CIRCUMFERENCE}
        transform="rotate(-90 50 50)"
        strokeLinecap="round"
        style={{
          transition: 'stroke-dashoffset 0.1s linear, stroke 0.3s ease-in-out',
        }}
      />
    </svg>
  );
};
